import React from 'react';
import { Link } from 'react-router-dom';
import { useQualifications } from '../../hooks';
import { Button, Card, CardHeader, CardBody, Spinner, Alert } from '../../components/ui';
import { LinkedDocuments, VerificationBadge, GuardedEditButton } from '../../components';
import {
  getFieldOfWorkLabel,
  formatLabel,
} from '../../utils';

export const Qualifications: React.FC = () => {
  const { qualifications, loading, error, deleteQualification, clearError } = useQualifications();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Qualifications</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your certificates, licences &amp; courses</p>
        </div>
        <Link to="/qualifications/add" className="self-start shrink-0">
          <Button>
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="danger" onClose={clearError}>{error}</Alert>
      )}

      {qualifications.length > 0 ? (
        <div className="space-y-4">
          {qualifications.map((qual) => {
            return (
            <Card key={qual.id} collapsible>
              <CardHeader>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-brand-50">
                    <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 break-words">{qual.title}</h3>
                    <div className="mt-1.5">
                      <VerificationBadge status={qual.verification_status} />
                    </div>
                    <p className="text-sm text-gray-600 font-medium mt-1 break-words">{qual.institution}</p>
                    <div className="flex gap-1.5 mt-2.5">
                      <GuardedEditButton to={`/qualifications/${qual.id}/edit`} status={qual.verification_status} size="icon">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span className="sr-only">Edit</span>
                      </GuardedEditButton>
                      <Button
                        variant="danger"
                        size="icon"
                        onClick={() => deleteQualification(qual.id)}
                        aria-label="Delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {qual.description && (
                  <p className="text-sm text-gray-500 leading-relaxed">{qual.description}</p>
                )}

                <div className={`flex flex-wrap gap-2 ${qual.description ? 'mt-3' : ''}`}>
                  {qual.qualification_type && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{formatLabel(qual.qualification_type)}</span>
                  )}
                  {qual.field_of_work && (
                    <span className="text-xs bg-brand-50 text-brand-600 px-2.5 py-1 rounded-full">{getFieldOfWorkLabel(qual.field_of_work)}</span>
                  )}
                  {qual.issue_date && (
                    <span className="text-xs bg-gray-50 text-gray-500 px-2.5 py-1 rounded-full border border-gray-100">
                      Issued {new Date(qual.issue_date).toLocaleDateString()}
                    </span>
                  )}
                  {qual.expiry_date && (
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${
                      new Date(qual.expiry_date) < new Date()
                        ? 'bg-red-50 text-red-600 border-red-100'
                        : 'bg-gray-50 text-gray-500 border-gray-100'
                    }`}>
                      Expires {new Date(qual.expiry_date).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {(qual.credential_id || qual.credential_url) && (
                  <div className="flex flex-wrap gap-4 mt-3 text-sm">
                    {qual.credential_id && (
                      <span className="text-xs text-gray-400">ID: {qual.credential_id}</span>
                    )}
                    {qual.credential_url && (
                      <a
                        href={/^https?:\/\//i.test(qual.credential_url!) ? qual.credential_url! : `https://${qual.credential_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-brand-600 font-semibold hover:text-brand-700 hover:underline"
                      >
                        View credential ↗
                      </a>
                    )}
                  </div>
                )}

                <LinkedDocuments
                  entityId={qual.id}
                  entityType="qualification"
                  defaultDocumentType="certificate"
                />
              </CardBody>
            </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">No qualifications yet</h3>
              <p className="text-sm text-gray-500 mb-5 max-w-xs mx-auto">Add your certificates, licences and courses to build your professional profile.</p>
              <Link to="/qualifications/add">
                <Button>Add Your First Qualification</Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};
